import {
   ONBOARDING_STEPS,
   AGENT_ONBOARDING_STEPS,
   STEP_LABELS,
} from '@forwardflow/constants/onboarding.constants'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { TOnboardingStep } from '@forwardflow/types/onboarding'
import { Tooltip } from '@mantine/core'
import { isOperatorEmail } from '@forwardflow/utils/app.utils'

export const Stepper = () => {
   const { onboardingData, goToStep, isAgent } = useOnboardingStoreV2()
   const currentStep = onboardingData.currentStep
   const { isSubscriptionActivated, isCalendarConnected, email } = onboardingData

   let visibleSteps = isAgent ? AGENT_ONBOARDING_STEPS : ONBOARDING_STEPS

   // Filter out subscription-related steps if subscription is activated
   if (isSubscriptionActivated) {
      visibleSteps = visibleSteps.filter(
         (step) =>
            !['plan-selection', 'fub-connect', 'payment-details'].includes(
               step
            )
      )
   }

   // Filter out calendar-connection step if calendar is connected
   if (isCalendarConnected) {
      visibleSteps = visibleSteps.filter(
         (step) => step !== 'calendar-connection'
      )
   }

   const currentStepIndex = visibleSteps.indexOf(currentStep)

   const handleStepClick = (step: TOnboardingStep, index: number) => {
      if (
         isOperatorEmail(email || '') ||
         onboardingData.isOnBoardingCompleted
      ) {
         goToStep(step)
         return
      }
      // Only allow clicking on steps that are before or at the current step
      if (index <= currentStepIndex) {
         goToStep(step)
      }
   }

   return (
      <div className="flex items-center gap-2">
         {visibleSteps.map((step, index) => {
            const isCurrent = index === currentStepIndex
            const isCompleted = index < currentStepIndex
            const isClickable =
               index <= currentStepIndex ||
               isOperatorEmail(email || '') ||
               onboardingData.isOnBoardingCompleted

            return (
               <Tooltip
                  label={STEP_LABELS[step]}
                  className="bg-black/90 text-white rounded-md p-2"
                  key={step}
               >
                  <button
                     onClick={() => handleStepClick(step, index)}
                     disabled={!isClickable}
                     className={`
                     w-3 h-3 rounded-full transition-all duration-200
                     ${isCurrent ? 'bg-white scale-110' : ''}
                     ${isCompleted ? 'bg-gray-500 hover:bg-gray-400' : ''}
                     ${!isCurrent && !isCompleted ? 'bg-gray-700' : ''}
                     ${
                        isClickable
                           ? 'cursor-pointer hover:scale-150'
                           : 'cursor-not-allowed'
                     }
                     ${isCurrent ? 'w-8' : 'w-2 h-2'}
                  `}
                     aria-label={`Step ${index + 1}: ${step}`}
                     title={step
                        .split('-')
                        .map(
                           (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                        )
                        .join(' ')}
                  />
               </Tooltip>
            )
         })}
      </div>
   )
}
